import React, { useState, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Plus } from 'lucide-react';

// Open modals, in mount order. Escape is answered by the LAST one only: two
// stacked modals (the SSH connection picker under the vault passphrase gate)
// used to race on whose capture listener was registered last — a re-render
// of the lower one with a fresh `onClose` moved its listener behind the
// upper one's, and Escape then closed the modal at the bottom.
const openModals = [];

/** True when Escape belongs to a Monaco find / suggest box open in the editor the key came from. */
export function monacoWidgetOpen(target) {
  const editor = target && typeof target.closest === 'function' ? target.closest('.monaco-editor') : null;
  return !!editor && !!editor.querySelector('.find-widget.visible, .suggest-widget.visible');
}

/** True while any shared Modal is open — shortcuts behind it must stand down. */
export function isModalOpen() {
  return openModals.length > 0;
}

const Modal = ({ isOpen, onClose, title, subtitle = '', children, type = 'default', preventBackdropClose = false, preventEscapeClose = false, customIcon = null, size = 'md', contentClassName = '', zIndex }) => {
  const { t } = useTranslation();
  const titleId = useId();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const [self] = useState(() => ({}));
  useEffect(() => {
    if (!isOpen) return undefined;
    openModals.push(self);
    return () => {
      const i = openModals.indexOf(self);
      if (i >= 0) openModals.splice(i, 1);
    };
  }, [isOpen, self]);

  useEffect(() => {
    if (!isOpen || preventEscapeClose) {
      return;
    }

    const handleEscKey = (event) => {
      // Escape inside a Monaco editor whose find / suggest widget is open
      // closes that widget first, as in VS Code — not the whole modal with
      // the edit in it (the DB Edit cell modal, 01.10.2026).
      if (event.key === 'Escape' && monacoWidgetOpen(event.target)) return;
      if (event.key === 'Escape' && openModals[openModals.length - 1] === self) {
        event.stopImmediatePropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey, true);
    return () => {
      document.removeEventListener('keydown', handleEscKey, true);
    };
  }, [isOpen, onClose, preventEscapeClose, self]);

  const handleBackdropClick = () => {
    if (!preventBackdropClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const getIcon = () => {
    if (customIcon) {
      return customIcon;
    }
    switch (type) {
      case 'danger':
        return <AlertTriangle size={24} className="text-red-500" />;
      case 'warning':
        return <AlertTriangle size={24} className="text-yellow-500" />;
      default:
        return null;
    }
  };
  // Portalled to <body> (07.10.2026): rendered in place, the overlay lived in
  // the page's stacking context and stopped under the title bar (sticky,
  // z-index 50) — the bar stayed bright above every dialog. At the root it
  // covers the whole window, like the app's popovers and the onboarding card.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={zIndex != null ? { zIndex } : undefined}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 modal-backdrop"
        onClick={handleBackdropClick}
      />      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative mx-4 w-full modal-content${contentClassName ? ` ${contentClassName}` : ''}`}
        style={{
          maxWidth:
            // 5xl: editors that need the room (the DB Edit cell modal). Opt-in.
            size === '5xl'
              ? 'min(72rem, 92vw)'
              : size === '3xl'
              ? '56rem'
              : size === '2xl'
              ? '46rem'
              : size === 'xl'
                ? '42rem'
                : size === 'lg'
                  ? '36rem'
                  : '30rem',
        }}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-inner">
            {getIcon()}
            <div>
              <h3 id={titleId} className="modal-header-title">{title}</h3>
              {subtitle && <p className="modal-header-subtitle">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>
        {/* Content */}
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

const PRIMARY_FULL_WIDTH_BUTTON_CLASS =
  'modern-button flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50';

const PromptModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  placeholder = '',
  defaultValue = '',
  hideCancel = false,
  submitLabel = 'Create',
  inputClassName = '',
}) => {
  const [value, setValue] = useState(defaultValue);

  const handleInputChange = useCallback((e) => {
    setValue(e.target.value);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim()) {
      onConfirm(value.trim());
      setValue('');
    }
  };

  const handleCancel = () => {
    setValue('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={title} customIcon={<Plus size={24} className="text-green-500" />}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {message && (
          <p className="text-sm text-gray-600 dark:text-zinc-300">{message}</p>
        )}          <input
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`modal-input${inputClassName ? ` ${inputClassName}` : ''}`}
          autoFocus
        />
        
        <div className={hideCancel ? '' : 'flex justify-end space-x-2 pt-2'}>
          {!hideCancel && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!value.trim()}
            className={
              hideCancel
                ? PRIMARY_FULL_WIDTH_BUTTON_CLASS
                : 'px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm'
            }
          >
            <span>{submitLabel}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel", type = "default", hideCancel = false }) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getConfirmButtonClass = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 text-white';
      default:
        return 'bg-blue-600 hover:bg-blue-700 text-white';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} type={type}>
      <div className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-zinc-300">{message}</p>

        <div className={hideCancel ? '' : 'flex justify-end space-x-2 pt-2'}>
          {!hideCancel && (
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`rounded-lg transition-colors modern-button text-sm ${getConfirmButtonClass()} ${hideCancel ? 'flex w-full items-center justify-center px-4 py-3' : 'px-3 py-1.5'}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export { Modal, PromptModal, ConfirmModal };
